package com.essntl.features.service.ui.allservice

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.PaginationState
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.collectSuccessEvent
import com.essntl.core.utils.viewmodel.getData
import com.essntl.core.utils.viewmodel.getPaginationData
import com.essntl.features.area.domain.repository.AreaRepository
import com.essntl.features.category.domain.repository.CategoryRepository
import com.essntl.features.profile.domain.use_case.GetCurrentUserUseCase
import com.essntl.features.service.domain.model.service.ServiceFilterOptions
import com.essntl.features.service.domain.repository.ServiceRepository
import com.essntl.features.service.domain.use_case.ExtraCategoryServiceUseCase
import com.essntl.features.tag.domain.repository.TagRepository
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*

class AllServiceViewModel(
    private val serviceRepository: ServiceRepository,
    private val extraCategoryServiceUseCase: ExtraCategoryServiceUseCase,
    private val categoryRepository: CategoryRepository,
    private val tagRepository: TagRepository,
    private val areaRepository: AreaRepository,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
    private val state: MutableStateFlow<AllServiceState>,
) : ViewModel {

    init {
        getCurrentProfile()
        getAllCategory()
        getAllTagList()
        getAllArea()

        collectSuccessEvent(
            flow = getCurrentUserUseCase.successEvent,
            getState = { state.value.getCurrentProfile },
            setState = { newState -> state.update { it.copy(getCurrentProfile = newState) } },
        )
    }

    private fun getCurrentProfile() = scope.launch { getCurrentUserUseCase.get() }

    fun refresh() {
        getAllCategory()
        getAllTagList()
        getAllArea()
    }

    fun onEvent(event: AllServiceEvent) {
        when (event) {
            is AllServiceEvent.GetAllCategory -> getAllCategory()
            is AllServiceEvent.Refresh -> refresh()
            is AllServiceEvent.SelectCategory -> selectCategory(event)
            is AllServiceEvent.GetAllService -> {
                getAllService(event.organizationId)
                getAllServiceCount()
            }

            is AllServiceEvent.LoadMoreServices -> loadMoreServices()
            is AllServiceEvent.DeleteService -> deleteService(event)
            is AllServiceEvent.SelectService -> selectService(event)
            is AllServiceEvent.EditFilterOptions -> editFilterOptions(event.filterOptions)
        }
    }

    private fun getAllCategory() =
        getData(
            getData = { categoryRepository.getAll() },
            getState = { state.value.getAllCategory },
            setState = { newState -> state.update { it.copy(getAllCategory = newState) } },
        )

    private fun getAllTagList() =
        getData(
            getData = { tagRepository.getAll() },
            getState = { state.value.getAllTag },
            setState = { newState -> state.update { it.copy(getAllTag = newState) } },
        )

    private fun selectCategory(event: AllServiceEvent.SelectCategory) {
        state.update {
            it.copy(selectedCategoryId = event.categoryId)
        }
    }

    private fun getAllService(organizationId: String? = null) {
        if (!organizationId.isNullOrEmpty()) {
            getData(
                getData = { serviceRepository.getServicesByOrganization(organizationId = organizationId) },
                getState = { state.value.getAllServiceOrganization },
                setState = { newState -> state.update { it.copy(getAllServiceOrganization = newState) } },
            )
        } else {
            state.update {
                it.copy(getAllService = PaginationState.loading())
            }

            loadMoreServices(ignoreLoading = true)
        }
    }

    private var loadMoreServicesJob: Job? = null
    private fun loadMoreServices(ignoreLoading: Boolean = false) {
        if (!ignoreLoading && state.value.getAllService.isLoading) return

        loadMoreServicesJob?.cancel()
        loadMoreServicesJob = getPaginationData(
            getData = {
                val nextPage = state.value.getAllService.loadedPages + 1L
                extraCategoryServiceUseCase
                    .getAllNonExtraServices(
                        page = nextPage,
                        filterOptions = state.value.filterOptions,
                    )
                    .map { list ->
                        list.filter { service ->
                            state.value.getAllService.data.none { it.id == service.id }
                        }
                    }
            },
            getState = { state.value.getAllService },
            setState = { newState -> state.update { it.copy(getAllService = newState) } },
            ignoreLoading = ignoreLoading,
        )
    }

    private var getAllServiceCountJob: Job? = null
    private fun getAllServiceCount() {
        getAllServiceCountJob?.cancel()
        getAllServiceCountJob = getData(
            getData = { serviceRepository.getCount(state.value.filterOptions) },
            getState = { state.value.getAllServiceCount },
            setState = { newState -> state.update { it.copy(getAllServiceCount = newState) } },
        )
    }

    private fun getAllArea() =
        getData(
            getData = { areaRepository.getAll() },
            getState = { state.value.getAllArea },
            setState = { newState -> state.update { it.copy(getAllArea = newState) } },
        )

    private fun deleteService(event: AllServiceEvent.DeleteService) =
        getData(
            getData = { serviceRepository.delete(event.service) },
            getState = { state.value.deleteServiceMap[event.service.id] ?: State() },
            setState = { newState ->
                state.update {
                    it.setDeleteServiceState(
                        serviceId = event.service.id,
                        deleteServiceState = newState,
                    )
                }
            },
            onSuccess = {
                state.update {
                    it
                        .removeDeleteServiceState(serviceId = event.service.id)
                        .copy(
                            getAllService = it.getAllService
                                .copy(
                                    data = it.getAllService.data
                                        .filter { service -> service.id != event.service.id },
                                ),
                        )
                }
            },
        )

    private fun selectService(event: AllServiceEvent.SelectService) {
        if (state.value.selectedServiceId == event.id) return

        state.update {
            it.copy(selectedServiceId = event.id)
        }
    }

    private fun editFilterOptions(filterOptions: ServiceFilterOptions) {
        if (filterOptions == state.value.filterOptions) return

        if (filterOptions.selectedCategoryIds != state.value.filterOptions.selectedCategoryIds)
            getAllServiceCount()

        state.update {
            it.copy(
                filterOptions = filterOptions,
            )
        }

        getAllService()
        getAllServiceCount()
    }
}